/**
 * @module components/Notifications/NotificationCenter
 * @description Panel de notificaciones in-app del usuario autenticado.
 *
 * Muestra todas las notificaciones del usuario ordenadas por fecha (más recientes primero).
 * Permite marcar como leídas individualmente o en bloque, y eliminar notificaciones.
 * El badge en el Header muestra el conteo de no leídas en tiempo real via Realtime de Supabase.
 *
 * Tipos de notificación: assignment, task_completed, evidence_uploaded, overdue, system
 *
 * @see DataContext para las funciones markNotificationRead() y deleteNotification()
 * @see Header que abre este panel al hacer clic en el ícono de campana
 */
import React from 'react';
import { X, Bell, CheckCircle, AlertTriangle, Info, AlertCircle, Trash2 } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { formatRelativeTime } from '../../utils/dateUtils';

interface NotificationCenterProps {
  onClose: () => void;
}

export function NotificationCenter({ onClose }: NotificationCenterProps) {
  const { notifications, markNotificationAsRead, markAllAsRead, deleteNotification, deleteAllRead } = useData();

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error': return <AlertCircle className="w-5 h-5 text-red-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getNotificationBgColor = (type: string) => {
    switch (type) {
      case 'success': return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'warning': return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'error': return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      default: return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
    }
  };

  const unreadNotifications = notifications.filter(n => !n.read);
  const readNotifications = notifications.filter(n => n.read);

  const handleNotificationClick = (notificationId: string) => {
    markNotificationAsRead(notificationId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-end z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-96 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Notificaciones
            </h2>
            {unreadNotifications.length > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {unreadNotifications.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Cerrar notificaciones"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-auto p-4">
          {notifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No tienes notificaciones
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Unread Notifications */}
              {unreadNotifications.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sin leer ({unreadNotifications.length})
                  </h3>
                  <div className="space-y-2">
                    {unreadNotifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-3 border rounded-lg hover:shadow-sm transition-all ${getNotificationBgColor(notification.type)}`}
                      >
                        <div className="flex items-start space-x-3">
                          {getNotificationIcon(notification.type)}
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => handleNotificationClick(notification.id)}
                          >
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {notification.title}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                              {formatRelativeTime(notification.timestamp)}
                            </p>
                            {/* Links desactivados - la app no usa enrutamiento
                            {notification.actionUrl && notification.actionText && (
                              <a
                                href={notification.actionUrl}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onClose();
                                }}
                                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline mt-1 inline-block"
                              >
                                {notification.actionText} →
                              </a>
                            )}
                            */}
                          </div>
                          <div className="flex items-center gap-2">
                            {!notification.read && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification.id);
                              }}
                              className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors p-1 rounded hover:bg-white/50 dark:hover:bg-gray-700/50"
                              aria-label="Eliminar notificación"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Read Notifications */}
              {readNotifications.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Leídas ({readNotifications.length})
                  </h3>
                  <div className="space-y-2">
                    {readNotifications.slice(0, 5).map((notification) => (
                      <div
                        key={notification.id}
                        className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg opacity-75"
                      >
                        <div className="flex items-start space-x-3">
                          {getNotificationIcon(notification.type)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {notification.title}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                              {formatRelativeTime(notification.timestamp)}
                            </p>
                            {/* Links desactivados - la app no usa enrutamiento
                            {notification.actionUrl && notification.actionText && (
                              <a
                                href={notification.actionUrl}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onClose();
                                }}
                                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline mt-1 inline-block"
                              >
                                {notification.actionText} →
                              </a>
                            )}
                            */}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors p-1 rounded hover:bg-white/50 dark:hover:bg-gray-700/50"
                            aria-label="Eliminar notificación"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {readNotifications.length > 5 && (
                      <p className="text-xs text-center text-gray-500 dark:text-gray-400 py-2">
                        y {readNotifications.length - 5} más...
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
            {unreadNotifications.length > 0 && (
              <button
                onClick={markAllAsRead}
                className="w-full px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
              >
                Marcar todas como leídas
              </button>
            )}
            {readNotifications.length > 0 && (
              <button
                onClick={deleteAllRead}
                className="w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors border border-red-200 dark:border-red-800"
              >
                Eliminar leídas ({readNotifications.length})
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}